// login-frontend/src/components/PetSim/PetGame.jsx
import React, { useEffect, useRef } from "react";
import Phaser from "phaser";
import gamebg from "../../assets/petSim/gamebg.png";
import dog from "../../assets/petSim/dog.png";
import bear from "../../assets/petSim/bear.png";
import chicken from "../../assets/petSim/chicken.png";
import avatarMaleD from "../../assets/petSim/character-male-d.png";
import avatarMaleE from "../../assets/petSim/character-male-e.png";
// food art
import carrotPng from "../../assets/petSim/food/carrot.png";
import tomatoPng from "../../assets/petSim/food/tomato.png";
import cabbagePng from "../../assets/petSim/food/cabbage.png";
import cherriesPng from "../../assets/petSim/food/cherries.png";
import hotdogPng from "../../assets/petSim/food/hot-dog.png";
// music
import bgm1 from "../../assets/petSim/music/petsim-bg-music.mp3";
import bgm2 from "../../assets/petSim/music/petsim-bg-music2.mp3";
import bgmRain from "../../assets/petSim/music/petsim-bg-rain.mp3";
// AI
import { PetBrain, encodeState, doAction, ACTIONS } from "./ai/petAi";
// coin art
import coinPng from "../../assets/petSim/coin/coin.png";

const API =
    (typeof window !== "undefined" && window.__ENV?.API_BASE) ||
    import.meta.env.VITE_API_BASE ||
    "";

class MainScene extends Phaser.Scene {
    constructor() {
        super("MainScene");
        this.mode = "walk"; // "wander" | "walk"
        this.player = null;
        this.pet = null;

        this.leash = { comfort: 80, max: 240 };
        this.speeds = { player: 180, petWalk: 120, petCatchup: 220 };
        this._wanderTimer = null;

        // === AI ===
        this.brain = new PetBrain({ alpha: 0.18, gamma: 0.92, epsilon: 0.08 });
        this.aiTickDefault = 450; // ms
        this._aiCooldown = 0;
        this._prevForUpdate = null;

        // === Economy & items ===
        // === Economy & items ===
        this.gold = 50;
        this.foods = [];              // dropped food instances
        this.placingFood = false;
        this.selectedFood = "carrot"; // default placement type
        this.inventory = { carrot: 0, tomato: 0, cabbage: 0, cherries: 0, hotdog: 0 };

        // === Sim time (clock) ===
        this.timeSpeed = 1;         // in-game minutes advanced per real second (1 = 1 min/sec)
        this.clockMin = 8 * 60;     // start at 08:00 (minutes since midnight)
        this._clockAccMs = 0;       // accumulator for delta -> minutes

        // === Sim time (existing) ===
        this.timeSpeed = 1;
        this._speedIdx = 0;
        this._speedSteps = [1, 2, 4];

        // === Coins (wander rewards) ===
        this.coinGroup = null;
        this._coinBatchSize = 10;

        this.FOOD_DEFS = [
            { key: "carrot", label: "Carrot", tex: "food_carrot", cost: 4 },
            { key: "tomato", label: "Tomato", tex: "food_tomato", cost: 4 },
            { key: "cabbage", label: "Cabbage", tex: "food_cabbage", cost: 5 },
            { key: "cherries", label: "Cherries", tex: "food_cherries", cost: 6 },
            { key: "hotdog", label: "Hot Dog", tex: "food_hotdog", cost: 7 },
        ];

    }

    preload() {
        this.load.image("bg", gamebg);
        this.load.image("dog", dog);
        this.load.image("bear", bear);
        this.load.image("chicken", chicken);
        this.load.image("avatar_male_d", avatarMaleD);
        this.load.image("avatar_male_e", avatarMaleE);
        this.load.image("food_carrot", carrotPng);
        this.load.image("food_tomato", tomatoPng);
        this.load.image("food_cabbage", cabbagePng);
        this.load.image("food_cherries", cherriesPng);
        this.load.image("food_hotdog", hotdogPng);
        this.load.audio("bgm1", bgm1);
        this.load.audio("bgm2", bgm2);
        this.load.audio("bgmRain", bgmRain);
        this.load.image("coin", coinPng);
    }

    async create() {
        const W = this.scale.width, H = this.scale.height;

        // background
        const bg = this.add.image(W / 2, H / 2, "bg").setDepth(-100);
        bg.setDisplaySize(W, H);
        this._bgSprite = bg;

        // environment overlays
        this._buildDayNight();
        // === MUSIC ===
        const tracks = ["bgm1", "bgm2"];
        this.currentBgmKey = Phaser.Utils.Array.GetRandom(tracks);
        this.currentBgm = this.sound.add(this.currentBgmKey, { loop: true, volume: 0.45 });
        this.rainBgm = this.sound.add("bgmRain", { loop: true, volume: 0.45 });
        this.currentBgm.play();
        this._audioReady = true;
        this._buildRain();
        // stop & purge any PetSim sounds when the scene shuts down/unmounts
        window.__phaserAudioCtx = this.sound.context;
        this.events.once("shutdown", () => {
            try { this.sound.stopAll(); this.sound.removeAll(); } catch { }
            try { this.sound.context?.close?.(); } catch { }  // fully close the ctx
            window.__phaserAudioCtx = null;                  // clear the global ref
        });
        this.sound.pauseOnBlur = false;

        // fetch pet data
        let petData = null;
        try {
            const res = await fetch(`${API}/api/pet/me`, { credentials: "include" });
            petData = await res.json();
        } catch { }

        const animalKey = petData?.species || "dog";
        this.physics.world.setBounds(0, 0, W, H);

        // player sprite
        let playerKey = "playerDot";
        const avatarKeyMap = { "male-d": "avatar_male_d", "male-e": "avatar_male_e" };
        const chosen = petData?.avatar && avatarKeyMap[petData.avatar];
        if (chosen) playerKey = chosen; else this._makePlayerTexture();

        this.player = this.physics.add.image(W * 0.5, H * 0.5 + 70, playerKey)
            .setCircle(12).setCollideWorldBounds(true).setDepth(10).setScale(1.0);

        // pet sprite
        this.pet = this.physics.add.image(W * 0.5, H * 0.5, animalKey).setScale(0.30);
        this.pet.setCollideWorldBounds(true).setDepth(10);
        this.pet.hunger = 0.4;
        this.pet.energy = 0.6;
        this.pet.boredom = 0.5;

        // idle pulse
        this.tweens.add({ targets: this.pet, scale: { from: 0.30, to: 0.315 }, duration: 900, yoyo: true, repeat: -1 });

        // HUD
        this._buildNeedsHUD();
        this._buildClockHUD();
        this._redrawClock();
        this._applyDayNight();

        // --- Coins: invisible pickups ---
        this.coinGroup = this.physics.add.staticGroup();
        this.physics.add.overlap(this.pet, this.coinGroup, (_pet, coin) => this._collectCoin(coin));

        // toast helper
        this.flash = (msg, color = "#ffb3b3") => {
            const t = this.add.text(W / 2, H - 18, msg, { fontSize: "14px", color })
                .setOrigin(0.5).setDepth(1001).setScrollFactor(0).setAlpha(0.95);
            this.tweens.add({ targets: t, alpha: 0, duration: 1600, delay: 700, onComplete: () => t.destroy() });
        };

        // === right-column UI ===
        const BTN_W = 170, BTN_H = 44, MARGIN = 20, GAP = 8;

        const makePill = (label, onClick) => {
            const c = this.add.container(0, 0).setDepth(2000).setScrollFactor(0);
            const bg = this.add.rectangle(0, 0, BTN_W, BTN_H, 0x000000, 0.55)
                .setOrigin(0, 0).setStrokeStyle(1, 0xffffff, 0.15);
            const tx = this.add.text(BTN_W / 2, BTN_H / 2, label, { fontSize: "14px", color: "#fff" }).setOrigin(0.5);
            c.add([bg, tx]);
            bg.setInteractive({ useHandCursor: true })
                .on("pointerover", () => bg.setFillStyle(0x111111, 0.65))
                .on("pointerout", () => bg.setFillStyle(0x000000, 0.55))
                .on("pointerdown", onClick);
            c.bg = bg; c.tx = tx;
            return c;
        };

        const btnManage = makePill("⚙️ Manage", () => window.location.href = "/Dashboard/PetSim/Pet");
        this._modeHost = makePill("Mode: Walk (M)", () => this.setMode(this.mode === "walk" ? "wander" : "walk"));
        const btnReturn = makePill("↩︎ Dashboard", () => window.location.href = "/Dashboard");
        this.btnShop = makePill("🛒 Shop", () => this._toggleShop());
        this.btnSpeed = makePill("Speed: 1×", () => this._cycleSpeed());
        this.btnWeather = makePill("Weather: Clear", () => {
            this._setRain(!this.isRaining);
            this.btnWeather.tx.setText(this.isRaining ? "Weather: Rain" : "Weather: Clear");
        });

        const placeRightColumn = (w) => {
            const x = w - BTN_W - MARGIN;
            const y0 = 12;
            btnManage.setPosition(x, y0);
            this._modeHost.setPosition(x, y0 + BTN_H + GAP);
            btnReturn.setPosition(x, y0 + (BTN_H + GAP) * 2);
            this.btnShop.setPosition(x, y0 + (BTN_H + GAP) * 3);
            this.btnSpeed.setPosition(x, y0 + (BTN_H + GAP) * 4);
            this.btnWeather.setPosition(x, y0 + (BTN_H + GAP) * 5);
        };
        placeRightColumn(W);

        // === Shop ===
        this._buildShopPanel();

        // === Input setup ===
        this.cursors = this.input.keyboard.createCursorKeys();
        this.keysWASD = this.input.keyboard.addKeys({
            W: Phaser.Input.Keyboard.KeyCodes.W,
            A: Phaser.Input.Keyboard.KeyCodes.A,
            S: Phaser.Input.Keyboard.KeyCodes.S,
            D: Phaser.Input.Keyboard.KeyCodes.D,
            SHIFT: Phaser.Input.Keyboard.KeyCodes.SHIFT,
            M: Phaser.Input.Keyboard.KeyCodes.M,
            ESC: Phaser.Input.Keyboard.KeyCodes.ESC,
        });

        this.input.keyboard.on("keydown-M", () =>
            this.setMode(this.mode === "walk" ? "wander" : "walk")
        );

        this.input.keyboard.on("keydown-ESC", () => {
            this.placingFood = false;
            this.input.setDefaultCursor("default");
            this._updateShopText();
        });

        // placing food on map
        this.input.on("pointerdown", (pointer) => {
            if (!this.placingFood) return;
            if (this.inventory[this.selectedFood] <= 0) {
                this.flash("No selected food in inventory");
                this.placingFood = false;
                this.input.setDefaultCursor("default");
                this._updateShopText();
                return;
            }
            const worldPoint = pointer.positionToCamera(this.cameras.main);
            this._dropFoodAt(worldPoint.x, worldPoint.y, this.selectedFood);
            this.inventory[this.selectedFood] -= 1;
            this._updateShopText();
            this._saveStateToBackend();
            if (this.inventory[this.selectedFood] <= 0) {
                this.placingFood = false;
                this.input.setDefaultCursor("default");
                this.flash("Out of stock for this item");
            }
        });

        // camera
        this.cameras.main.setBounds(0, 0, W, H);

        // load persisted game state
        await this._loadStateFromBackend();
        this.setMode(this.mode || "walk");
        this._redrawClock();
        this._redrawHUD();
        this._applyDayNight();

        // resize handler
        this.scale.on("resize", (size) => {
            const { width, height } = size;
            bg.setDisplaySize(width, height).setPosition(width / 2, height / 2);
            this.physics.world.setBounds(0, 0, width, height);
            placeRightColumn(width);
            if (!this.hud) this._buildNeedsHUD();
            this._redrawHUD();
            if (this.sky) this.sky.setSize(width, height);
            if (this.shopScrim) this.shopScrim.setSize(width, height);
            if (this.shopPanel?.visible) {
                const cx = (width - this._shopW) / 2;
                const cy = (height - this._shopH) / 2;
                this.shopPanel.setPosition(cx, cy);
            }
            this._screenW = width;
            this._screenH = height;
            if (this.rainLayer) this.rainLayer.setPosition(0, 0);
            this._applyDayNight();
        });

        // brain load + autosave
        this.brain.load();
        this.time.addEvent({ delay: 10000, loop: true, callback: () => this.brain?.save?.() });

        // autosave state every 5s
        this.time.addEvent({
            delay: 5000,
            loop: true,
            callback: () => this._saveStateToBackend()
        });

        // save on unload / reload
        this._onBeforeUnloadState = () => { try { this._saveStateToBackend(); } catch { } };
        window.addEventListener("beforeunload", this._onBeforeUnloadState);
        this.events.once("shutdown", () => {
            window.removeEventListener("beforeunload", this._onBeforeUnloadState);
        });

        // camera follow
        this.cameras.main.startFollow(this.player, true, 0.08, 0.08);
    }

    update(time, delta) {
        this._advanceTime(delta);
        this._redrawClock();
        this._redrawHUD();
        this._updateRain(delta);

        if (!this.cursors || !this.keysWASD) return;

        // ⬇️ Move avatar in BOTH modes
        this._updatePlayer(delta);

        if (this.mode === "walk") {
            this._updatePetFollow(delta);
        } else {
            this._updatePetWander(delta);
            this._runAi(delta);
        }
    }

    // =========================
    // AI main loop
    // =========================
    _runAi(dt) {
        if (!this.pet) return;
        this._aiCooldown -= dt;
        if (this._aiCooldown > 0) return;

        const worldNow = this.snapshotWorld();
        const petNow = this.snapshotPet();

        // === Survival override ===
        const HUNGER_TH = 0.7;
        const hasFood = !!this._nearestFood();
        if (petNow.hunger >= HUNGER_TH) {
            // prioritize eating if possible, else go home and wait
            const duration = hasFood ? this.petSeekFood(this.pet) : this.petGoHome(this.pet);
            this._aiCooldown = (duration ?? this.aiTickDefault);

            // still drift needs a bit (no Q update on hard override)
            this._needsDrift(hasFood ? "seekFood" : "idle");
            // remember a snapshot to keep the TD chain sane next time
            this._prevForUpdate = { prevSnap: petNow, prevKey: this.encodeForAI(petNow, worldNow), prevAIdx: 0 };
            return;
        }

        // === Normal learned behavior ===
        const sKey = this.encodeForAI(petNow, worldNow);
        const aIdx = this.brain.act(sKey);
        const duration = doAction(this, this.pet, ACTIONS[aIdx]);
        this._aiCooldown = (duration ?? this.aiTickDefault);

        if (this._prevForUpdate) {
            const { prevSnap, prevKey, prevAIdx } = this._prevForUpdate;
            this.brain.step(prevSnap, petNow, prevAIdx, prevKey, sKey);
        }
        this._prevForUpdate = { prevSnap: petNow, prevKey: sKey, prevAIdx: aIdx };

        this._needsDrift(ACTIONS[aIdx]);
    }

    _needsDrift(action) {
        if (!this.pet) return;
        const p = this.pet;

        // ENERGY: moving consumes energy; napping restores it
        let eDelta = 0;
        if (action === "nap") eDelta = +0.060;
        else if (action === "play") eDelta = -0.020;
        else if (action === "seekFood" || action === "followPlayer") eDelta = -0.014;
        else if (action === "wander") eDelta = -0.010;
        else /* idle */ eDelta = -0.004;
        p.energy = Phaser.Math.Clamp((p.energy ?? 0.6) + eDelta, 0, 1);

        // BOREDOM: movement reduces boredom, idling & napping increase a bit
        let bDelta = 0.0;
        if (action === "play") bDelta = -0.120;
        else if (action === "followPlayer") bDelta = -0.030;
        else if (action === "wander" || action === "seekFood") bDelta = -0.020;
        else if (action === "nap" || action === "idle") bDelta = +0.010;
        p.boredom = Phaser.Math.Clamp((p.boredom ?? 0.5) + bDelta, 0, 1);
    }

    snapshotPet() {
        if (!this.pet) return { hunger: 0.4, energy: 0.6, boredom: 0.5 };
        return {
            hunger: this.pet?.hunger ?? 0.4,
            energy: this.pet?.energy ?? 0.6,
            boredom: this.pet?.boredom ?? 0.5,
        };
    }
    snapshotWorld() {
        const player = this.player || { x: 0, y: 0 };
        const p = this.pet || { x: 0, y: 0 };
        const dist = (a, b) => Math.hypot((a.x - b.x), (a.y - b.y));

        // nearest dropped food distance
        let nearestFoodDist = 99999;
        for (const f of this.foods) {
            if (!f.sprite.active) continue;
            const d = dist(p, f.sprite);
            if (d < nearestFoodDist) nearestFoodDist = d;
        }

        return {
            distToFood: nearestFoodDist,
            distToPlayer: dist(p, player),
            isDay: true,
        };
    }
    encodeForAI(petSnap, worldSnap) { return encodeState(petSnap, worldSnap); }

    // =========================
    // Modes
    // =========================
    setMode(next) {
        if (this.mode === next) return;
        this.mode = next;

        // reset velocities
        if (this.player) this.player.setVelocity(0, 0);
        if (this.pet) this.pet.setVelocity(0, 0);

        // clear wander timer if switching away
        if (this._wanderTimer) { this._wanderTimer.remove(false); this._wanderTimer = null; }

        if (next === "walk") {
            this.cameras.main.startFollow(this.player, true, 0.1, 0.1);
            this._modeHost.tx.setText("Mode: Walk (M)");
            this.time.delayedCall(10, () => this._teleportIfTooFar());
        } else {
            this.cameras.main.stopFollow();
            this.cameras.main.centerOn(this.pet.x, this.pet.y);
            this._modeHost.tx.setText("Mode: Wander (M)");
            this._startWanderTimer();
            this._maybeSpawnCoinBatch();
        }
    }

    // =========================
    // Movement & action helpers
    // =========================
    _updatePlayer(delta) {
        const SHIFT_DOWN = !!(this.keysWASD?.SHIFT?.isDown);
        const accel = SHIFT_DOWN ? 1.35 : 1.0;
        const speed = this.speeds.player * accel;

        let vx = 0, vy = 0;

        const left = !!(this.cursors?.left?.isDown || this.keysWASD?.A?.isDown);
        const right = !!(this.cursors?.right?.isDown || this.keysWASD?.D?.isDown);
        const up = !!(this.cursors?.up?.isDown || this.keysWASD?.W?.isDown);
        const down = !!(this.cursors?.down?.isDown || this.keysWASD?.S?.isDown);

        if (left) vx -= speed;
        if (right) vx += speed;
        if (up) vy -= speed;
        if (down) vy += speed;

        if (vx && vy) { const inv = 1 / Math.sqrt(2); vx *= inv; vy *= inv; }
        this.player?.setVelocity(vx, vy);
    }

    _updatePetFollow(delta) {
        if (!this.pet || !this.player) return;
        const dx = this.player.x - this.pet.x;
        const dy = this.player.y - this.pet.y;
        const dist = Math.hypot(dx, dy);

        if (dist < this.leash.comfort) { this.pet.setVelocity(0, 0); return; }

        const base = dist > this.leash.max ? this.speeds.petCatchup : this.speeds.petWalk;
        const arrive = Phaser.Math.Clamp(dist / this.leash.max, 0.4, 1.0);
        const speed = base * arrive;
        const nx = dx / (dist || 1), ny = dy / (dist || 1);

        if (this.pet.body.blocked.left || this.pet.body.blocked.right || this.pet.body.blocked.up || this.pet.body.blocked.down) {
            const rot = Phaser.Math.DegToRad(Phaser.Math.Between(-35, 35));
            const cos = Math.cos(rot), sin = Math.sin(rot);
            const rx = nx * cos - ny * sin;
            const ry = nx * sin + ny * cos;
            this.pet.setVelocity(rx * speed, ry * speed);
        } else {
            this.pet.setVelocity(nx * speed, ny * speed);
        }
    }

    _updatePetWander() {
        if (!this.pet) return;
        if (this.pet.body.blocked.left || this.pet.body.blocked.right || this.pet.body.blocked.up || this.pet.body.blocked.down) {
            this._nudgeWander();
        }
    }

    _startWanderTimer() {
        this._wanderTimer = this.time.addEvent({
            delay: Phaser.Math.Between(2000, 3500),
            loop: true,
            callback: () => {
                const pause = Math.random() < 0.3;
                if (pause) { this.pet.setVelocity(0, 0); return; }
                this._nudgeWander();
            },
        });
        this._nudgeWander();
    }

    _nudgeWander() {
        const curAngle = Math.atan2(this.pet.body.velocity.y, this.pet.body.velocity.x);
        const turn = Phaser.Math.DegToRad(Phaser.Math.Between(-60, 60));
        const angle = isFinite(curAngle) ? Phaser.Math.Angle.Wrap(curAngle + turn) : Phaser.Math.DegToRad(Phaser.Math.Between(0, 359));
        const v = new Phaser.Math.Vector2();
        this.physics.velocityFromRotation(angle, this.speeds.petWalk, v);
        this.pet.setVelocity(v.x, v.y);
    }

    _teleportIfTooFar() {
        const dx = this.player.x - this.pet.x;
        const dy = this.player.y - this.pet.y;
        const dist = Math.hypot(dx, dy);
        if (dist > this.leash.max * 2.2) {
            const offset = 28;
            this.pet.setPosition(this.player.x - Math.sign(dx || 1) * offset, this.player.y - Math.sign(dy || 1) * offset);
        }
    }

    _makePlayerTexture() {
        const g = this.make.graphics({ x: 0, y: 0, add: false });
        g.fillStyle(0x4aa3ff, 1); g.fillCircle(12, 12, 12);
        g.lineStyle(2, 0xffffff, 1); g.strokeCircle(12, 12, 12);
        g.generateTexture("playerDot", 24, 24); g.destroy();
    }

    _makeFoodTexture() {
        const g = this.make.graphics({ x: 0, y: 0, add: false });
        g.fillStyle(0xffc07a, 1);
        g.fillCircle(8, 8, 8);
        g.lineStyle(2, 0x8b4f10, 1);
        g.strokeCircle(8, 8, 8);
        g.generateTexture("food", 16, 16);
        g.destroy();
    }

    // --- Day/Night layer ---
    _buildDayNight() {
        // overlay that darkens/tones the world, but sits under all HUD/UI
        this.sky = this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x001a33, 0.0)
            .setOrigin(0, 0)
            .setScrollFactor(0)
            .setDepth(50); // world ~10, HUD ~100, UI ~2000 → 50 darkens sprites, not UI

        // cache bg sprite for tinting
        this._bgSprite = this._bgSprite || this.children.list.find(o => o.texture?.key === "bg");
    }

    _applyDayNight() {
        if (!this.sky) return;

        // minutes → [0..1) day phase, noon = 0.5
        const t = (this.clockMin % (24 * 60)) / (24 * 60);
        // cosine curve:  dayAmount=1 at noon, 0 at midnight
        const dayAmount = (Math.cos((t - 0.5) * Math.PI * 2) + 1) / 2;
        const nightness = 1 - dayAmount; // 0=bright day, 1=deep night

        // overlay alpha: subtle by day, stronger at night
        const base = 0.06, extra = 0.44;
        this.sky.fillColor = 0x001a33;
        this.sky.setAlpha(base + extra * nightness);

        // background tint: blend toward cooler tone at night
        const lerpColor = (a, b, k) => {
            const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255;
            const br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
            const r = Math.round(ar + (br - ar) * nightness);
            const g = Math.round(ag + (bg - ag) * nightness);
            const bl = Math.round(ab + (bb - ab) * nightness);
            return (r << 16) | (g << 8) | bl;
        };
        const dayTint = 0xffffff;
        const nightTint = 0xb7c8ff; // slightly cool
        if (this._bgSprite) this._bgSprite.setTint(lerpColor(dayTint, nightTint, nightness));
    }

    // --- Clock HUD ---
    _buildClockHUD() {
        // small pill under your needs HUD, left top
        this.clockHUD = this.add.container(16, 110).setDepth(100).setScrollFactor(0);
        const bg = this.add.rectangle(0, 0, 132, 26, 0x000000, 0.45).setOrigin(0, 0);
        bg.setStrokeStyle(1, 0xffffff, 0.12);
        this.clockText = this.add.text(8, 6, "", { fontSize: "12px", color: "#fff" });
        this.clockHUD.add([bg, this.clockText]);
    }
    _redrawClock() {
        if (!this.clockText) return;
        const hh = Math.floor(this.clockMin / 60) % 24;
        const mm = Math.floor(this.clockMin % 60);
        const pad = (n) => (n < 10 ? "0" + n : "" + n);
        this.clockText.setText(`🕒  ${pad(hh)}:${pad(mm)}`);
    }

    // --- Time stepper: convert real ms -> in-game minutes and apply slow hunger drift ---
    _advanceTime(deltaMs) {
        if (!this.pet) return;
        // advance in-game minutes according to rate
        this._clockAccMs += deltaMs;
        const msPerGameMinute = 1000 / this.timeSpeed;
        if (this._clockAccMs < msPerGameMinute) return;
        const steps = Math.floor(this._clockAccMs / msPerGameMinute);
        this._clockAccMs -= steps * msPerGameMinute;
        this.clockMin = (this.clockMin + steps) % (24 * 60);
        this._applyDayNight();
        const hungerPerMinute = 0.1 / 60;
        this.pet.hunger = Math.min(1, (this.pet.hunger ?? 0.4) + hungerPerMinute * steps);
    }

    _getStateSnapshot() {
        // serialize minimal, robust state (numbers only where possible)
        return {
            version: 1,
            gold: this.gold,
            inventory: this.inventory,
            clockMin: this.clockMin,
            timeSpeed: this.timeSpeed,
            mode: this.mode,
            pet: {
                x: Math.round(this.pet?.x || 0),
                y: Math.round(this.pet?.y || 0),
                hunger: this.pet?.hunger ?? 0.4,
                energy: this.pet?.energy ?? 0.6,
                boredom: this.pet?.boredom ?? 0.5,
            },
            player: {
                x: Math.round(this.player?.x || 0),
                y: Math.round(this.player?.y || 0),
            },
            foods: this.foods
                .filter(f => f?.sprite?.active)
                .slice(-40)
                .map(f => ({ x: Math.round(f.sprite.x), y: Math.round(f.sprite.y), type: f.type || 'carrot' })),

            // NEW: store remaining hidden coins
            coins: (this.coinGroup?.getChildren?.() || [])
                .map(c => ({ x: Math.round(c.x), y: Math.round(c.y), v: Number(c.getData("value")) || 1 }))
                .slice(0, this._coinBatchSize),
        };
    }

    _applyStateSnapshot(s) {
        if (!s || typeof s !== 'object') return;

        this.gold = Number.isFinite(s.gold) ? s.gold : this.gold;
        if (s.inventory && typeof s.inventory === 'object') {
            this.inventory = { ...this.inventory, ...s.inventory };
        }
        if (Number.isFinite(s.clockMin)) this.clockMin = s.clockMin;
        if (Number.isFinite(s.timeSpeed)) {
            this.timeSpeed = s.timeSpeed;
            if (this.btnSpeed?.tx) this.btnSpeed.tx.setText(`Speed: ${this.timeSpeed}×`);
        }
        if (s.mode === 'walk' || s.mode === 'wander') this.mode = s.mode;

        // position + needs
        if (s.player && Number.isFinite(s.player.x) && Number.isFinite(s.player.y)) {
            this.player?.setPosition(s.player.x, s.player.y);
        }
        if (s.pet) {
            if (Number.isFinite(s.pet.x) && Number.isFinite(s.pet.y)) {
                this.pet?.setPosition(s.pet.x, s.pet.y);
            }
            if (this.pet) {
                this.pet.hunger = Math.max(0, Math.min(1, s.pet.hunger ?? this.pet.hunger));
                this.pet.energy = Math.max(0, Math.min(1, s.pet.energy ?? this.pet.energy));
                this.pet.boredom = Math.max(0, Math.min(1, s.pet.boredom ?? this.pet.boredom));
            }
        }

        // rebuild dropped foods WITHOUT consuming inventory or showing a toast
        if (Array.isArray(s.foods)) {
            for (const f of s.foods) {
                const def = this.FOOD_DEFS.find(d => d.key === f.type) || this.FOOD_DEFS[0];
                const spr = this.physics.add.image(f.x, f.y, def.tex).setDepth(9).setScale(0.9);
                spr.setImmovable(true);
                spr.setAlpha(1);
                this.foods.push({ sprite: spr, type: def.key });
            }
        }

        if (Array.isArray(s.coins)) {
            this.coinGroup?.clear(true, true);
            for (const c of s.coins) {
                const spt = this.coinGroup.create(c.x, c.y, "coin")
                    .setOrigin(0.5)
                    .setScale(0.65)
                    .setAlpha(0); // invisible
                spt.body.setCircle(8, spt.displayOriginX - 8, spt.displayOriginY - 8);
                spt.refreshBody();
                spt.setData("value", Number(c.v) || 1);
            }
        }

        this._applyDayNight();
        this._redrawClock();
        this._redrawHUD();
        this._maybeSpawnCoinBatch();
    }

    async _loadStateFromBackend() {
        try {
            const res = await fetch(`${API}/api/pet/state`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                this._applyStateSnapshot(data);
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }

    async _saveStateToBackend() {
        if (!this.player || !this.pet) return;

        try {
            const payload = this._getStateSnapshot();
            const res = await fetch(`${API}/api/pet/state`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) console.warn("Failed to save state:", res.status);
        } catch (err) {
            console.warn("Save state error", err);
        }
    }


    // === AI action endpoints (return ms to hold before next decision)
    petIdle(p) { p.setVelocity?.(0, 0); return 350; }
    petWander(p) { this._nudgeWander(); p.boredom = Math.max(0, (p.boredom ?? 0.5) - 0.02); return 600; }

    petGoHome(p) {
        const target = this.HOME_POS || { x: this.scale.width * 0.23, y: this.scale.height * 0.73 };
        const a = Phaser.Math.Angle.Between(p.x, p.y, target.x, target.y);
        const d = Phaser.Math.Distance.Between(p.x, p.y, target.x, target.y);

        if (d > 20) {
            const sp = 110;
            p.setVelocity(Math.cos(a) * sp, Math.sin(a) * sp);
        } else {
            // arrived: wait here
            p.setVelocity(0, 0);
            if (!this._homeHint || this.time.now - this._homeHint > 4000) {
                this._homeHint = this.time.now;
                this.flash("Hungry—waiting at home for food…", "#ffd36b");
            }

        }
        return 520; // how long until next AI tick
    }

    // Move to nearest *dropped* food and eat it
    petSeekFood(p) {
        const target = this._nearestFood();
        if (!target) {
            // no food placed—still count as light movement
            const a = Phaser.Math.FloatBetween(0, Math.PI * 2);
            const sp = 90;
            p.setVelocity(Math.cos(a) * sp, Math.sin(a) * sp);
            return 520;
        }

        const a = Phaser.Math.Angle.Between(p.x, p.y, target.x, target.y);
        const sp = 110;
        p.setVelocity(Math.cos(a) * sp, Math.sin(a) * sp);

        const d = Phaser.Math.Distance.Between(p.x, p.y, target.x, target.y);
        if (d < 18) {
            target.destroy();
            this.pet.hunger = Math.max(0, (this.pet.hunger ?? 0.4) - 0.30); // 30%
            this.pet.energy = Math.min(1, (this.pet.energy ?? 0.6) + 0.05);
            this.flash("Nom nom!", "#e6ffb3");
            this._saveStateToBackend();
        }

        return 520;
    }

    petNap(p) { p.setVelocity(0, 0); p.energy = Math.min(1, (p.energy ?? 0.6) + 0.06); p.boredom = Math.min(1, (p.boredom ?? 0.5) + 0.01); return 700; }
    petPlay(p) {
        const toy = this.toy || { x: this.scale.width * 0.75, y: this.scale.height * 0.6 };
        const a = Phaser.Math.Angle.Between(p.x, p.y, toy.x, toy.y);
        const sp = 120;
        p.setVelocity(Math.cos(a) * sp, Math.sin(a) * sp);
        if (Phaser.Math.Distance.Between(p.x, p.y, toy.x, toy.y) < 28) {
            p.boredom = Math.max(0, (p.boredom ?? 0.5) - 0.15);
            p.energy = Math.max(0, (p.energy ?? 0.6) - 0.03);
        }
        return 520;
    }
    petFollowPlayer(p) {
        const player = this.player || { x: p.x, y: p.y };
        const a = Phaser.Math.Angle.Between(p.x, p.y, player.x, player.y);
        const sp = 110;
        p.setVelocity(Math.cos(a) * sp, Math.sin(a) * sp);
        p.boredom = Math.max(0, (p.boredom ?? 0.5) - 0.01);
        return 550;
    }

    // =========================
    // Food helpers
    // =========================
    _nearestFood() {
        let best = null, bestD = 99999;
        for (const f of this.foods) {
            if (!f.sprite.active) continue;
            const d = Phaser.Math.Distance.Between(this.pet.x, this.pet.y, f.sprite.x, f.sprite.y);
            if (d < bestD) { bestD = d; best = f.sprite; }
        }
        return best;
    }
    _dropFoodAt(x, y, key = "carrot") {
        const def = this.FOOD_DEFS.find(f => f.key === key) || this.FOOD_DEFS[0];
        const spr = this.physics.add.image(x, y, def.tex).setDepth(9).setScale(0.9);
        spr.setImmovable(true);
        // subtle pop animation
        spr.setAlpha(0); this.tweens.add({ targets: spr, alpha: 1, duration: 180 });
        this.foods.push({ sprite: spr, type: key });
        this.flash(`${def.label} placed`, "#b3ffd9");
    }


    // =========================
    // HUD (needs bars)
    // =========================
    _buildNeedsHUD() {
        // container
        this.hud = this.add.container(16, 44).setDepth(100).setScrollFactor(0);

        const bg = this.add.rectangle(0, 0, 230, 58, 0x000000, 0.45).setOrigin(0, 0);
        bg.setStrokeStyle(1, 0xffffff, 0.12);

        const label = (txt, y) => this.add.text(8, y, txt, { fontSize: "12px", color: "#fff" });
        const barBG = (y) => this.add.rectangle(70, y, 148, 6, 0xffffff, 0.12).setOrigin(0, 0.5);
        const barFG = (y) => this.add.rectangle(70, y, 1, 6, 0xff3b3b, 0.95).setOrigin(0, 0.5);

        // hunger (red fill means more hunger; we invert when drawing)
        this.lblH = label("Hunger", 16);
        this.hBarBG = barBG(16);
        this.hBarFG = barFG(16);

        // energy (blue)
        this.lblE = label("Energy", 32);
        this.eBarBG = barBG(32);
        this.eBarFG = this.add.rectangle(70, 32, 1, 6, 0x3ba7ff, 0.95).setOrigin(0, 0.5);

        // boredom (yellow)
        this.lblB = label("Boredom", 48);
        this.bBarBG = barBG(48);
        this.bBarFG = this.add.rectangle(70, 48, 1, 6, 0xffe26b, 0.95).setOrigin(0, 0.5);

        this.hud.add([bg, this.lblH, this.hBarBG, this.hBarFG, this.lblE, this.eBarBG, this.eBarFG, this.lblB, this.bBarBG, this.bBarFG]);
    }

    _redrawHUD() {
        // build-on-demand or bail if not ready
        if (!this.hud || !this.hBarFG || !this.eBarFG || !this.bBarFG) {
            if (!this.hud) this._buildNeedsHUD();
            if (!this.hBarFG || !this.eBarFG || !this.bBarFG) return;
        }
        const p = this.snapshotPet();     // <- uses safe defaults
        const maxW = 148, clamp = (v) => Math.max(0, Math.min(maxW, v));
        this.hBarFG.width = clamp(p.hunger * maxW);
        this.eBarFG.width = clamp(p.energy * maxW);
        this.bBarFG.width = clamp(p.boredom * maxW);
    }

    // =========================
    // Shop & inventory UI
    // =========================
    _buildShopPanel() {
        // sizes
        this._shopW = 520;
        this._shopH = 380;

        // --- scrim (darken world behind) ---
        this.shopScrim = this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0.35)
            .setOrigin(0, 0)
            .setScrollFactor(0)
            .setDepth(1998)
            .setVisible(false)
            .setInteractive({ useHandCursor: false })
            .on("pointerdown", () => this._toggleShop(false)); // click outside to close

        // --- panel container ---
        const panel = this.add.container(0, 0).setDepth(2001).setScrollFactor(0);
        const w = this._shopW, h = this._shopH;

        // BG FIRST → behind everything
        const bg = this.add.rectangle(0, 0, w, h, 0x0a0a0a, 0.9).setOrigin(0, 0);
        bg.setStrokeStyle(1, 0xffffff, 0.15);
        panel.add(bg);

        // Header
        const head = this.add.rectangle(0, 0, w, 44, 0x151515, 0.98).setOrigin(0, 0);
        head.setStrokeStyle(1, 0xffffff, 0.12);
        const title = this.add.text(12, 12, "Shop & Inventory", { fontSize: "15px", color: "#fff", fontStyle: "bold" });
        this.txtGold = this.add.text(w - 12, 12, "", { fontSize: "14px", color: "#ffd36b" }).setOrigin(1, 0);
        this.txtStatus = this.add.text(w - 12, 28, "", { fontSize: "11px", color: "#cfcfcf" }).setOrigin(1, 0);
        this.txtHint = this.add.text(12, h - 24, "Tip: In Inventory, click Select to place. Press ESC to cancel.", { fontSize: "11px", color: "#9a9a9a" });
        panel.add(this.txtHint);
        panel.add([head, title, this.txtGold]);

        // Tabs
        const tabBar = this.add.container(0, 44);
        const mkTabBtn = (x, label, onClick) => {
            const b = this.add.container(x, 0);
            const r = this.add.rectangle(0, 0, 110, 28, 0x1b1b1b, 1).setOrigin(0, 0).setStrokeStyle(1, 0xffffff, 0.12);
            const t = this.add.text(55, 14, label, { fontSize: "12px", color: "#fff" }).setOrigin(0.5);
            r.setInteractive({ useHandCursor: true })
                .on("pointerover", () => r.setFillStyle(0x232323, 1))
                .on("pointerout", () => r.setFillStyle(0x1b1b1b, 1))
                .on("pointerdown", onClick);
            b.add([r, t]); return b;
        };

        // content roots
        this.shopContent = this.add.container(0, 72);      // Shop tab
        this.invContent = this.add.container(0, 72).setVisible(false); // Inventory tab

        const btnShopTab = mkTabBtn(12, "Shop", () => this._setShopTab("shop"));
        const btnInvTab = mkTabBtn(128, "Inventory", () => this._setShopTab("inv"));
        tabBar.add([btnShopTab, btnInvTab]);
        panel.add([tabBar, this.shopContent, this.invContent]);

        // Helper: small button
        this._mkSmallButton = (x, y, bw, bh, label, onClick) => {
            const b = this.add.container(x, y);
            const br = this.add.rectangle(0, 0, bw, bh, 0x222222, 1).setOrigin(0, 0).setStrokeStyle(1, 0xffffff, 0.12);
            const bt = this.add.text(bw / 2, bh / 2, label, { fontSize: "11px", color: "#fff" }).setOrigin(0.5);
            br.setInteractive({ useHandCursor: true })
                .on("pointerover", () => br.setFillStyle(0x2b2b2b, 1))
                .on("pointerout", () => br.setFillStyle(0x222222, 1))
                .on("pointerdown", onClick);
            b.add([br, bt]); return b;
        };

        // ===== SHOP GRID (buy only) =====
        const sCardW = 156, sCardH = 72, sGapX = 12, sGapY = 12, sLeft = 12, sTop = 8, sCols = 3;
        const buildShopCard = (i, def) => {
            const col = i % sCols, row = Math.floor(i / sCols);
            const x = sLeft + col * (sCardW + sGapX);
            const y = sTop + row * (sCardH + sGapY);
            const card = this.add.container(x, y);

            const r = this.add.rectangle(0, 0, sCardW, sCardH, 0x141414, 0.98)
                .setOrigin(0, 0).setStrokeStyle(1, 0xffffff, 0.10);
            const icon = this.add.image(10, sCardH / 2, def.tex).setOrigin(0, 0.5).setScale(0.7);
            const name = this.add.text(66, 10, def.label, { fontSize: "13px", color: "#fff" });
            const price = this.add.text(66, 28, `$${def.cost}`, { fontSize: "12px", color: "#a0ffa8" });
            const buyBtn = this._mkSmallButton(sCardW - 60, sCardH - 24, 48, 18, "Buy", () => {
                if (this.gold < def.cost) { this.flash("Not enough gold"); return; }
                this.gold -= def.cost;
                this.inventory[def.key] += 1;
                this._updateShopText();
                this._rebuildInventoryCards();
                this._onGoldChanged();
                this._saveStateToBackend();
            });
            card.add([r, icon, name, price, buyBtn]);
            r.setInteractive({ useHandCursor: true })
                .on("pointerover", () => this.tweens.add({ targets: r, alpha: 1, duration: 120 }))
                .on("pointerout", () => this.tweens.add({ targets: r, alpha: 0.98, duration: 160 }));
            this.shopContent.add(card);
        };
        this.FOOD_DEFS.forEach((d, i) => buildShopCard(i, d));

        // ===== INVENTORY GRID (select to place) =====
        const iCardW = 156, iCardH = 72, iGapX = 12, iGapY = 12, iLeft = 12, iTop = 8, iCols = 3;

        this._rebuildInventoryCards = () => {
            // clear old
            this.invContent.removeAll(true);
            this.invCards = [];

            const buildInvCard = (i, def) => {
                const col = i % iCols, row = Math.floor(i / iCols);
                const x = iLeft + col * (iCardW + iGapX);
                const y = iTop + row * (iCardH + iGapY);
                const card = this.add.container(x, y);

                const r = this.add.rectangle(0, 0, iCardW, iCardH, 0x141414, 0.98)
                    .setOrigin(0, 0).setStrokeStyle(1, 0xffffff, 0.10);
                const icon = this.add.image(10, iCardH / 2, def.tex).setOrigin(0, 0.5).setScale(0.7);
                const name = this.add.text(66, 10, def.label, { fontSize: "13px", color: "#fff" });
                const count = this.add.text(66, 28, `In bag: ${this.inventory[def.key]}`, { fontSize: "12px", color: "#d4d4d4" });

                const selectBtn = this._mkSmallButton(iCardW - 76, iCardH - 24, 64, 18, "Select", () => {
                    if (this.inventory[def.key] <= 0) { this.flash("No stock"); return; }
                    this._enterPlacingMode(def.key); // cursor → food, close panel
                });

                card.add([r, icon, name, count, selectBtn]);

                r.setInteractive({ useHandCursor: true })
                    .on("pointerover", () => this.tweens.add({ targets: r, alpha: 1, duration: 120 }))
                    .on("pointerout", () => this.tweens.add({ targets: r, alpha: 0.98, duration: 160 }));

                this.invContent.add(card);
                this.invCards.push({ def, countText: count });
            };

            this.FOOD_DEFS.forEach((d, i) => buildInvCard(i, d));
        };
        this._rebuildInventoryCards();

        // footer hint
        this.txtSel = this.add.text(12, h - 24, "Tip: In Inventory, click Select to place. Press ESC to cancel.", { fontSize: "11px", color: "#9a9a9a" });
        panel.add(this.txtSel);

        panel.setVisible(false);
        this.shopPanel = panel;
        this._setShopTab("shop"); // default
        this._updateShopText();
    }


    _setShopTab(tab) {
        const isShop = tab === "shop";
        this.shopContent?.setVisible(isShop);
        this.invContent?.setVisible(!isShop);
    }

    _updateShopText() {
        // gold
        this.txtGold?.setText(`$${this.gold}`);

        // inventory counts on cards
        if (this.invCards) {
            for (const { def, countText } of this.invCards) {
                countText?.setText(`In bag: ${this.inventory[def.key]}`);
            }
        }

        // status line (selected / placing)
        if (this.txtStatus) {
            const label = this.FOOD_DEFS.find(f => f.key === this.selectedFood)?.label || this.selectedFood;
            this.txtStatus.setText(this.placingFood ? `Placing: ${label}` : `Selected: ${label}`);
        }
    }

    // Build a small 24x24 cursor from a loaded texture key and apply it.
    // Returns true if successful.
    _setCursorToFood(key) {
        const def = this.FOOD_DEFS.find(f => f.key === key);
        if (!def) return false;

        const tex = this.textures.get(def.tex);
        if (!tex) return false;

        const img = tex.getSourceImage();
        if (!img) return false;

        // Downscale to a browser-friendly size (many UAs cap at ~32x32)
        const SIZE = 24;
        const cx = document.createElement("canvas");
        cx.width = SIZE; cx.height = SIZE;
        const ctx = cx.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(img, 0, 0, SIZE, SIZE);

        const url = cx.toDataURL("image/png");
        // Hotspot in the center so it "points" naturally; change to (0,0) if you prefer top-left
        this.input.setDefaultCursor(`url(${url}) ${SIZE / 2} ${SIZE / 2}, pointer`);
        return true;
    }

    _cycleSpeed() {
        this._speedIdx = (this._speedIdx + 1) % this._speedSteps.length;
        this.timeSpeed = this._speedSteps[this._speedIdx];
        if (this.btnSpeed?.tx) this.btnSpeed.tx.setText(`Speed: ${this.timeSpeed}×`);
        this.flash(`Time speed → ${this.timeSpeed}×`, "#d5e8ff");
        this._saveStateToBackend();
    }

    // Synchronous canvas textures (no async decoding → no green boxes)
    _makeRainTexture() {
        if (!this.textures.exists("rainDrop")) {
            const w = 2, h = 18;
            const tex = this.textures.createCanvas("rainDrop", w, h);
            const ctx = tex.getContext();
            const g = ctx.createLinearGradient(0, 0, 0, h);
            g.addColorStop(0.00, "rgba(170,208,255,0.00)");
            g.addColorStop(0.20, "rgba(170,208,255,0.35)");
            g.addColorStop(0.60, "rgba(170,208,255,0.65)");
            g.addColorStop(1.00, "rgba(170,208,255,0.10)");
            ctx.clearRect(0, 0, w, h);
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, w, h);
            tex.refresh();
        }

        // splash
        if (!this.textures.exists("rainSplash")) {
            const cw = 14, ch = 4;
            const tex2 = this.textures.createCanvas("rainSplash", cw, ch);
            const ctx2 = tex2.getContext();
            ctx2.clearRect(0, 0, cw, ch);
            ctx2.fillStyle = "rgba(170,208,255,0.75)";
            ctx2.beginPath();
            ctx2.ellipse(cw / 2, ch / 2, cw / 2, ch / 3, 0, 0, Math.PI * 2);
            ctx2.fill();
            tex2.refresh();
        }
    }


    // Build a simple screen-space rain layer (no Phaser particles API)
    _buildRain() {
        this._makeRainTexture();

        this._screenW = this.scale.width;
        this._screenH = this.scale.height;
        // Container above day/night (50) but below HUD (~100)
        this.rainLayer = this.add.container(0, 0).setScrollFactor(0).setDepth(60);
        // Layer specs: [density divisor, speed scale, alpha, rotation deg]
        const specs = [
            { div: 14, sp: 0.55, alpha: 0.35, rot: 68 }, // far (slow, faint)
            { div: 10, sp: 0.85, alpha: 0.55, rot: 70 }, // mid
            { div: 7, sp: 1.10, alpha: 0.75, rot: 72 }, // near (fast, bright)
        ];

        this._rainLayers = [];

        for (const spec of specs) {
            const count = Math.round((this._screenW / spec.div));
            const drops = [];
            for (let i = 0; i < count; i++) {
                const x = Math.random() * this._screenW;
                const y = Math.random() * this._screenH - this._screenH;
                const img = this.add.image(x, y, "rainDrop")
                    .setOrigin(0.5)
                    .setScrollFactor(0)
                    .setAlpha(spec.alpha);
                img.setRotation(Phaser.Math.DegToRad(spec.rot));
                // base velocity (px/s), angled down-left
                const vx = (-100 - Math.random() * 80) * spec.sp; // wind left
                const vy = (420 + Math.random() * 220) * spec.sp; // fall speed
                drops.push({ img, vx, vy, rot: spec.rot });
                this.rainLayer.add(img);
            }
            this._rainLayers.push({ drops, spec });
        }

        // wind / gust controls
        this._windT = 0;
        this._windBase = -40;
        this._windGust = 0;

        this.isRaining = false;
        this._rainIntensity = 1;
        this.rainLayer.setVisible(false);
    }

    // Toggle rain
    _setRain(on) {
        this.isRaining = !!on;
        if (this.rainLayer) this.rainLayer.setVisible(this.isRaining);
        if (!this._audioReady) return;
        if (this.isRaining) {
            // fade out normal bgm, fade in rain music
            if (this.currentBgm && this.currentBgm.isPlaying) {
                this.tweens.add({
                    targets: this.currentBgm,
                    volume: 0,
                    duration: 1200,
                    onComplete: () => this.currentBgm.stop(),
                });
            }
            if (this.rainBgm && !this.rainBgm.isPlaying) {
                this.rainBgm.setVolume(0);
                this.rainBgm.play();
                this.tweens.add({ targets: this.rainBgm, volume: 0.45, duration: 1200 });
            }
        } else {
            // fade out rain, resume a random chill track
            if (this.rainBgm && this.rainBgm.isPlaying) {
                this.tweens.add({
                    targets: this.rainBgm,
                    volume: 0,
                    duration: 1200,
                    onComplete: () => this.rainBgm.stop(),
                });
            }
            const next = Phaser.Utils.Array.GetRandom(["bgm1", "bgm2"]);
            this.currentBgmKey = next;
            this.currentBgm = this.sound.add(next, { loop: true, volume: 0 });
            this.currentBgm.play();
            this.tweens.add({ targets: this.currentBgm, volume: 0.45, duration: 1200 });
        }
    }

    // Adjust intensity (0..1): changes velocity and spawn density feel
    _setRainIntensity(k = 1) {
        this._rainIntensity = Phaser.Math.Clamp(k, 0, 1);
        if (!this._rainLayers) return;
        // scale alpha a bit with intensity
        for (const { drops, spec } of this._rainLayers) {
            const aBase = spec.alpha;
            const a = aBase * (0.6 + 0.8 * this._rainIntensity); // 60%..140%
            drops.forEach(d => d.img.setAlpha(Math.min(1, a)));
            // optionally scale speeds subtly (feels heavier)
            drops.forEach(d => {
                d.vxBase ??= d.vx; d.vyBase ??= d.vy;
                d.vx = d.vxBase * (0.85 + 0.5 * this._rainIntensity);
                d.vy = d.vyBase * (0.85 + 0.5 * this._rainIntensity);
            });
        }
    }

    // Per-frame rain update
    _updateRain(deltaMs) {
        if (!this.isRaining || !this._rainLayers) return;
        const dt = Math.min(0.05, deltaMs / 1000); // clamp big frames
        const w = this._screenW || this.scale.width;
        const h = this._screenH || this.scale.height;

        // wind oscillation (slow sine) + occasional gusts
        this._windT += dt;
        const sinWind = Math.sin(this._windT * 0.7) * 25; // gently sway
        // decay gust toward 0
        this._windGust += (0 - this._windGust) * Math.min(1, dt * 1.8);
        // random gust trigger
        if (Math.random() < 0.003) this._windGust += Phaser.Math.Between(-80, -30);

        const windX = (this._windBase + sinWind + this._windGust) * (0.7 + 0.6 * this._rainIntensity);

        for (const layer of this._rainLayers) {
            const { drops, spec } = layer;
            for (const d of drops) {
                const vx = d.vx + windX * spec.sp;
                const vy = d.vy;
                d.img.x += vx * dt;
                d.img.y += vy * dt;

                // Slight rotation adjust to match wind
                const targetRot = Phaser.Math.DegToRad(d.rot + Phaser.Math.Clamp((windX / -120), -6, 4));
                d.img.rotation += (targetRot - d.img.rotation) * 0.15;

                // wrap & splash when hitting bottom / left
                const offBottom = d.img.y > h + 12;
                const offLeft = d.img.x < -16;
                if (offBottom || offLeft) {
                    // splash only if bottom
                    if (offBottom) {
                        const s = this.add.image(d.img.x, h - 4, "rainSplash")
                            .setOrigin(0.5, 1).setAlpha(0.7).setDepth(this.rainLayer.depth + 1).setScrollFactor(0);
                        s.scaleX = 0.6 + Math.random() * 0.6;
                        s.scaleY = 1;
                        this.tweens.add({
                            targets: s,
                            alpha: 0,
                            scaleX: s.scaleX * 1.8,
                            duration: 180,
                            onComplete: () => s.destroy()
                        });
                    }
                    // respawn above top at random x
                    d.img.x = Math.random() * (w + 40) - 20;
                    d.img.y = -20 - Math.random() * 120;
                }
            }
        }
    }

    // Pet collided with an invisible coin
    _collectCoin(coin) {
        const value = Number(coin.getData("value")) || 1;
        this.gold += value;
        this._updateShopText?.();

        // Nice feedback: sparkle at coin + popup over pet
        const spark = this.add.rectangle(coin.x, coin.y, 8, 8, 0xffffcc, 1)
            .setDepth(999).setAlpha(0.9);
        this.tweens.add({
            targets: spark, alpha: 0, scaleX: 2.0, scaleY: 2.0, duration: 220,
            onComplete: () => spark.destroy()
        });

        // Floating text near the pet: “Found a coin! +$X”
        if (this.pet) {
            const t = this.add.text(this.pet.x, this.pet.y - 26, `Found a coin! +$${value}`, {
                fontSize: "13px", color: "#a0ffa8"
            }).setOrigin(0.5).setDepth(1001);
            this.tweens.add({
                targets: t, y: t.y - 18, alpha: 0, duration: 900,
                onComplete: () => t.destroy()
            });
        } else {
            this.flash(`+$${value}`, "#a0ffa8");
        }

        coin.destroy();
        this._onGoldChanged();
        this._saveStateToBackend();
    }


    _onGoldChanged() {
        // If we’re poor again *and* there are no coins on the map,
        // allow another batch to spawn (e.g., after spending in shop).
        this._maybeSpawnCoinBatch();
    }

    _shouldSpawnBatch() {
        if (!this.coinGroup) return false;
        const countNow = this.coinGroup.getChildren().length;
        // Only spawn when *broke* and there are currently no coins on the map
        return (this.gold < 30) && (countNow === 0);
    }

    _maybeSpawnCoinBatch() {
        if (!this._shouldSpawnBatch()) return;
        const pad = 24;
        const W = this.scale.width, H = this.scale.height;
        const values = [1, 3, 5];

        for (let i = 0; i < this._coinBatchSize; i++) {
            const x = Phaser.Math.Between(pad, W - pad);
            const y = Phaser.Math.Between(pad, H - pad);
            const s = this.coinGroup.create(x, y, "coin")
                .setOrigin(0.5)
                .setScale(0.65)
                .setAlpha(0); // invisible by design
            s.body.setCircle(8, s.displayOriginX - 8, s.displayOriginY - 8);
            s.refreshBody();
            s.setData("value", Phaser.Utils.Array.GetRandom(values)); // 1/3/5
        }

        // (Optional) subtle hint so players know wandering can help
        this.flash("Your pet senses something shiny nearby…", "#d5e8ff");
    }


    // turn cursor into selected food, close modal, and enter placing mode
    _enterPlacingMode(key) {
        this.selectedFood = key;

        // set cursor preview now (nice UX)
        const ok = this._setCursorToFood(key);
        if (!ok) this.input.setDefaultCursor("crosshair");

        // close modal immediately
        this._toggleShop(false);

        // Enable placing ONLY AFTER the current click is released.
        // This prevents the select-click from placing at the button's location.
        this.placingFood = false;
        this.input.once("pointerup", () => {
            this.placingFood = true;
        });

        const label = this.FOOD_DEFS.find(f => f.key === key)?.label || key;
        this.flash(`Placing ${label}… click the map`, "#d5e8ff");
    }

    _refreshCard(invTextObj, key) {
        invTextObj.setText(`In bag: ${this.inventory[key]}`);
    }

    _toggleShop(force) {
        const show = typeof force === "boolean" ? force : !this.shopPanel.visible;
        this.shopPanel.setVisible(show);
        this.shopScrim.setVisible(show);

        if (show) {
            // center
            const cx = (this.scale.width - this._shopW) / 2;
            const cy = (this.scale.height - this._shopH) / 2;
            this.shopPanel.setPosition(cx, cy);

            // cancel any active placing + reset cursor
            this.placingFood = false;
            this.input.setDefaultCursor("default");
        }
        this._updateShopText();
    }
}

export default function PetGame() {
    const hostRef = useRef(null);
    const gameRef = useRef(null);
    useEffect(() => {
        const parent = hostRef.current;
        if (!parent) return;

        const DPR = Math.min(2, window.devicePixelRatio || 1); // crisp, but still fast

        const config = {
            type: Phaser.AUTO,
            parent,
            width: parent.clientWidth || window.innerWidth,
            height: parent.clientHeight || window.innerHeight,
            resolution: DPR,            // ⬅️ HiDPI rendering
            pixelArt: true,             // ⬅️ crisp pixel look (set false if you prefer smooth)
            render: {
                antialias: false,         // ⬅️ crisper pixels (set true if pixelArt=false)
                roundPixels: true,        // ⬅️ reduces subpixel blur
            },
            physics: { default: "arcade", arcade: { gravity: { y: 0 }, debug: false } },
            scene: [MainScene],
            scale: { mode: Phaser.Scale.RESIZE, autoCenter: Phaser.Scale.CENTER_BOTH },
            backgroundColor: "#000000",
        };

        const game = new Phaser.Game(config);
        gameRef.current = game;
        return () => {
            const ctx = game.sound?.context;
            try { game.sound?.stopAll(); game.sound?.removeAll(); } catch { }
            try { game.destroy(true); } catch { }
            // fully close the old WebAudio context so it can’t “hold” the tab
            try { ctx?.close?.(); } catch { }
            gameRef.current = null;
        };
    }, []);


    return (
        <div className="pet-wrap">
            <div className="pet-game-host" ref={hostRef} />
        </div>
    );
}












